// @ts-check
// Canvas do editor de blocos (spec/email.md seção 19.2): o e-mail em 600 px com as cores do e-mail (não as do tema),
// seções, colunas e blocos selecionáveis, alças de arrastar, zonas de soltar ("Solte o bloco aqui"), o "+" entre os
// blocos e a barra do item selecionado (mover, duplicar, excluir). O bloco HTML é desenhado num iframe sandbox=""
// (RISCO-23). O texto é editado no próprio canvas.
import { html } from 'htm/preact';
import { EMAIL_FONT_STACKS, EMAIL_SECTION_LAYOUTS } from '../../../../../shared/email-design/email-document.js';
import { estimateHtmlHeight } from '../../../../../shared/email-design/render-email.js';
import { Icon } from '../../../ui/icon.js';
import { LightIconButton } from '../../../ui/button.js';
import { EmailRichTextEditor, BLOCK_TEXT_TOOLS } from '../email-rich-text-editor.js';
import { SandboxedHtmlFrame } from '../sandboxed-html-frame.js';
import { knownEmailAssetUrl } from '../local-preview.js';
import { blockPaletteKey, BLOCK_ICONS } from './block-catalog.js';
import { t } from '../../../i18n/index.js';

/**
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailDocument} EmailDocument
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailBlock} EmailBlock
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailSection} EmailSection
 * @typedef {import('../../../../../shared/email-design/document-operations.js').BlockTarget} BlockTarget
 * @typedef {{ kind: 'BLOCK', id: string } | { kind: 'SECTION', id: string } | { kind: 'COLUMN', sectionId: string, columnIndex: number } | null} EditorSelection
 * @typedef {{ kind: 'BLOCK', target: BlockTarget } | { kind: 'SECTION', index: number }} DropTarget
 * @typedef {{ kind: 'PALETTE', key: string, itemKind: 'SECTION' | 'BLOCK' } | { kind: 'BLOCK', id: string } | { kind: 'SECTION', id: string }} DragPayload
 * @typedef {{
 *   document: EmailDocument, readOnly: boolean, brandColor: string, logoUrl: string | null, baseUrl: string,
 *   catalog: import('../../../data/email-templates.js').EmailVariableCatalog | null,
 *   selection: EditorSelection, select: (selection: EditorSelection) => void,
 *   dragging: DragPayload | null, startDrag: (payload: DragPayload, event: DragEvent, image?: Element | null) => void, endDrag: () => void,
 *   activeDrop: string | null, setActiveDrop: (key: string | null) => void, drop: (target: DropTarget) => void,
 *   openInsertMenu: (anchor: HTMLElement, target: DropTarget) => void,
 *   blockAction: (action: 'UP' | 'DOWN' | 'DUPLICATE' | 'DELETE', blockId: string) => void,
 *   canMoveBlock: (blockId: string, direction: -1 | 1) => boolean,
 *   sectionAction: (action: 'UP' | 'DOWN' | 'DUPLICATE' | 'DELETE', sectionId: string) => void,
 *   updateBlock: (blockId: string, patch: Record<string, unknown>, coalesceKey?: string) => void,
 *   openImageLibrary: (blockId: string) => void,
 *   problemsByBlock: Map<string, { errors: string[], warnings: string[] }>,
 *   readingOrder: Map<string, number>,
 * }} CanvasEditor
 */

/** Endereço da imagem no canvas: a biblioteca (no modo demonstração, o data: PNG), a URL ou o logo atual. @param {EmailBlock & { type: 'IMAGE' }} block @param {CanvasEditor} editor */
export function canvasImageUrl(block, editor) {
  const source = block.source;
  if (!source) return null;
  if (source.kind === 'ASSET') return knownEmailAssetUrl(source.assetId) ?? `${editor.baseUrl}/e/i/${source.assetId}.${source.extension}`;
  if (source.kind === 'URL') return source.url;
  return editor.logoUrl;
}

/** @param {string} color @param {string} brandColor */
const resolveColor = (color, brandColor) => (color === 'BRAND' ? brandColor : color);

/** @param {{ top: number, right: number, bottom: number, left: number }} padding */
const paddingStyle = (padding) => `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`;

/** @param {DropTarget} target */
const dropKey = (target) => (target.kind === 'SECTION' ? `section:${target.index}` : `block:${target.target.sectionId}:${target.target.columnIndex}:${target.target.index}`);

/**
 * Zona entre blocos ou seções: durante o arraste, recebe a solta; fora dele, mostra o "+".
 * @param {{ editor: CanvasEditor, target: DropTarget, variant?: 'BLOCK' | 'SECTION' }} props
 */
function InsertZone({ editor, target, variant = 'BLOCK' }) {
  if (editor.readOnly) return null;
  const key = dropKey(target);
  const dragging = editor.dragging;
  const accepts = dragging
    && (target.kind === 'SECTION' || dragging.kind === 'BLOCK' || (dragging.kind === 'PALETTE' && dragging.itemKind === 'BLOCK'));
  if (dragging) {
    if (!accepts) return null;
    return html`<div
      class="email-drop-zone"
      data-variant=${variant}
      data-active=${editor.activeDrop === key ? 'true' : undefined}
      onDragOver=${(/** @type {DragEvent} */ event) => {
        event.preventDefault();
        // o efeito tem de caber no effectAllowed do arraste, senão o navegador não solta
        if (event.dataTransfer) event.dataTransfer.dropEffect = dragging.kind === 'PALETTE' ? 'copy' : 'move';
        if (editor.activeDrop !== key) editor.setActiveDrop(key);
      }}
      onDragLeave=${() => editor.activeDrop === key && editor.setActiveDrop(null)}
      onDrop=${(/** @type {DragEvent} */ event) => {
        event.preventDefault();
        event.stopPropagation();
        editor.drop(target);
      }}
    >${t('emailDesign.dropHere')}</div>`;
  }
  return html`<div class="email-insert-line" data-variant=${variant}>
    <button
      type="button"
      class="email-insert-button"
      aria-label=${variant === 'SECTION' ? t('emailDesign.toolbar.addSection') : t('emailDesign.toolbar.add')}
      title=${variant === 'SECTION' ? t('emailDesign.toolbar.addSection') : t('emailDesign.toolbar.add')}
      onClick=${(/** @type {MouseEvent} */ event) => {
        event.stopPropagation();
        editor.openInsertMenu(/** @type {HTMLElement} */ (event.currentTarget), target);
      }}
    ><${Icon} name="IconPlus" size=${12} /></button>
  </div>`;
}

/**
 * Barra do item selecionado.
 * @param {{ label: string, onAction: (action: 'UP' | 'DOWN' | 'DUPLICATE' | 'DELETE') => void, onDragStart: (event: DragEvent) => void,
 *   onDragEnd: () => void, canMoveUp: boolean, canMoveDown: boolean }} props
 */
function ItemToolbar({ label, onAction, onDragStart, onDragEnd, canMoveUp, canMoveDown }) {
  return html`<div class="email-item-toolbar" role="toolbar" aria-label=${label} onClick=${(/** @type {MouseEvent} */ event) => event.stopPropagation()}>
    <span class="email-item-toolbar-label">${label}</span>
    <span class="email-drag-handle" draggable="true" role="button" tabindex="-1" aria-label=${t('emailDesign.toolbar.drag')} title=${t('emailDesign.toolbar.drag')}
      onDragStart=${onDragStart} onDragEnd=${onDragEnd}><${Icon} name="IconGripVertical" size=${14} /></span>
    <${LightIconButton} icon="IconArrowUp" ariaLabel=${t('emailDesign.toolbar.moveUp')} disabled=${!canMoveUp} onClick=${() => onAction('UP')} />
    <${LightIconButton} icon="IconArrowDown" ariaLabel=${t('emailDesign.toolbar.moveDown')} disabled=${!canMoveDown} onClick=${() => onAction('DOWN')} />
    <${LightIconButton} icon="IconCopy" ariaLabel=${t('emailDesign.toolbar.duplicate')} onClick=${() => onAction('DUPLICATE')} />
    <${LightIconButton} icon="IconTrash" ariaLabel=${t('emailDesign.toolbar.delete')} onClick=${() => onAction('DELETE')} />
  </div>`;
}

/**
 * Conteúdo de um bloco, como aparece no e-mail.
 * @param {{ block: EmailBlock, editor: CanvasEditor, columnWidth: number, isSelected: boolean }} props
 */
export function BlockContent({ block, editor, columnWidth, isSelected }) {
  const style = editor.document.style;
  switch (block.type) {
    case 'TEXT':
      return html`<${EmailRichTextEditor}
        html=${block.html}
        catalog=${editor.catalog}
        tools=${BLOCK_TEXT_TOOLS}
        toolbar="FLOATING"
        readOnly=${editor.readOnly}
        brandColor=${editor.brandColor}
        ariaLabel=${t('emailDesign.palette.TEXT')}
        placeholder=${t('emailDesign.textPlaceholder')}
        className="email-canvas-text"
        onFocusChange=${(/** @type {boolean} */ focused) => focused && !isSelected && editor.select({ kind: 'BLOCK', id: block.id })}
        onChange=${(/** @type {string} */ value) => editor.updateBlock(block.id, { html: value }, `text:${block.id}`)}
      />`;
    case 'IMAGE': {
      const url = canvasImageUrl(block, editor);
      const isLogo = block.source?.kind === 'INSTITUTION_LOGO';
      const available = Math.max(20, columnWidth - block.padding.left - block.padding.right);
      if (!url) {
        return html`<div class="email-canvas-image-placeholder" style=${{ textAlign: block.align.toLowerCase() }}>
          <${Icon} name=${isLogo ? 'IconBuildingCommunity' : 'IconPhoto'} size=${24} />
          <span>${isLogo ? t('emailDesign.logoMissing') : t('emailDesign.imageMissing')}</span>
          ${!editor.readOnly && !isLogo
            ? html`<button type="button" class="email-link-button" onClick=${(/** @type {MouseEvent} */ event) => { event.stopPropagation(); editor.openImageLibrary(block.id); }}>
                ${t('emailDesign.properties.chooseImage')}
              </button>`
            : null}
        </div>`;
      }
      return html`<div class="email-canvas-image" style=${{ textAlign: block.align.toLowerCase() }}>
        <img src=${url} alt=${block.alt} draggable="false"
          style=${{ width: block.width === 'FULL' ? '100%' : `${Math.min(block.width, available)}px`, maxWidth: '100%' }} />
      </div>`;
    }
    case 'BUTTON': {
      const background = resolveColor(block.backgroundColor, editor.brandColor);
      return html`<div class="email-canvas-button-row" style=${{ textAlign: block.align.toLowerCase() }}>
        <span class="email-canvas-button" data-full-width=${block.fullWidth ? 'true' : undefined} data-empty=${block.label.trim() ? undefined : 'true'}
          style=${{ background, color: block.textColor, borderRadius: `${block.borderRadius}px`, fontSize: `${block.fontSize}px`, fontFamily: EMAIL_FONT_STACKS[style.fontFamily] }}>
          ${block.label.trim() || t('emailDesign.buttonPlaceholder')}
        </span>
      </div>`;
    }
    case 'DIVIDER':
      return html`<div class="email-canvas-divider" style=${{ borderTop: `${block.thickness}px solid ${block.color}`, width: `${block.widthPercent}%` }}></div>`;
    case 'SPACER':
      return html`<div class="email-canvas-spacer" style=${{ height: `${block.height}px` }} aria-hidden="true"></div>`;
    case 'HTML': {
      if (!block.html.trim()) return html`<div class="email-canvas-html-empty"><${Icon} name="IconCode" size=${20} /><span>${t('emailDesign.htmlEmpty')}</span></div>`;
      const height = Math.min(800, Math.max(48, estimateHtmlHeight(block.html, columnWidth)));
      const source = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;font-family:${EMAIL_FONT_STACKS[style.fontFamily]};color:${style.textColor};font-size:${style.fontSize}px;">${block.html}</body></html>`;
      return html`<div class="email-canvas-html">
        <${SandboxedHtmlFrame} html=${source} title=${t('emailDesign.palette.HTML')} height=${height} />
        <span class="email-canvas-html-shield" aria-hidden="true"></span>
      </div>`;
    }
    default:
      return null;
  }
}

/**
 * @param {{ block: EmailBlock, editor: CanvasEditor, columnWidth: number }} props
 */
function CanvasBlock({ block, editor, columnWidth }) {
  const isSelected = editor.selection?.kind === 'BLOCK' && editor.selection.id === block.id;
  const problems = editor.problemsByBlock.get(block.id);
  const label = t(`emailDesign.palette.${blockPaletteKey(block)}`);
  const position = editor.readingOrder.get(block.id) ?? 0;
  const padding = 'padding' in block ? paddingStyle(block.padding) : '0';
  return html`<div
    class="email-canvas-block"
    data-type=${block.type}
    data-block-id=${block.id}
    data-selected=${isSelected ? 'true' : undefined}
    data-error=${problems?.errors.length ? 'true' : undefined}
    data-dragging=${editor.dragging?.kind === 'BLOCK' && editor.dragging.id === block.id ? 'true' : undefined}
    tabindex="0"
    role="group"
    aria-label=${t('emailDesign.blockLabel', { type: label, position })}
    style=${{ padding }}
    onClick=${(/** @type {MouseEvent} */ event) => {
      event.stopPropagation();
      if (!isSelected) editor.select({ kind: 'BLOCK', id: block.id });
    }}
    onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        editor.select({ kind: 'BLOCK', id: block.id });
      } else if ((event.key === 'Delete' || event.key === 'Backspace') && !editor.readOnly) {
        event.preventDefault();
        editor.blockAction('DELETE', block.id);
      } else if (event.key === 'Escape') {
        editor.select(null);
      }
    }}
  >
    ${isSelected && !editor.readOnly
      ? html`<${ItemToolbar}
          label=${label}
          canMoveUp=${editor.canMoveBlock(block.id, -1)}
          canMoveDown=${editor.canMoveBlock(block.id, 1)}
          onAction=${(/** @type {'UP' | 'DOWN' | 'DUPLICATE' | 'DELETE'} */ action) => editor.blockAction(action, block.id)}
          onDragStart=${(/** @type {DragEvent} */ event) => editor.startDrag({ kind: 'BLOCK', id: block.id }, event, /** @type {Element} */ (event.currentTarget).closest('.email-canvas-block'))}
          onDragEnd=${editor.endDrag}
        />`
      : null}
    ${problems?.errors.length
      ? html`<span class="email-block-alert" title=${problems.errors.map((code) => t(`emailTemplates.problems.${code}`)).join('\n')}><${Icon} name="IconAlertTriangle" size=${14} /></span>`
      : null}
    <${BlockContent} block=${block} editor=${editor} columnWidth=${columnWidth} isSelected=${isSelected} />
  </div>`;
}

/**
 * @param {{ section: EmailSection, sectionIndex: number, editor: CanvasEditor }} props
 */
function CanvasSection({ section, sectionIndex, editor }) {
  const isSelected = editor.selection?.kind === 'SECTION' && editor.selection.id === section.id;
  const widths = EMAIL_SECTION_LAYOUTS[section.layout] ?? [100];
  const sectionCount = editor.document.sections?.length ?? 0;
  const innerWidth = 600 - section.padding.left - section.padding.right;
  return html`<div
    class="email-canvas-section"
    data-selected=${isSelected ? 'true' : undefined}
    data-section-id=${section.id}
    data-dragging=${editor.dragging?.kind === 'SECTION' && editor.dragging.id === section.id ? 'true' : undefined}
  >
    ${!editor.readOnly
      ? isSelected
        ? html`<${ItemToolbar}
            label=${t('emailDesign.sectionLabel', { position: sectionIndex + 1 })}
            canMoveUp=${sectionIndex > 0}
            canMoveDown=${sectionIndex < sectionCount - 1}
            onAction=${(/** @type {'UP' | 'DOWN' | 'DUPLICATE' | 'DELETE'} */ action) => editor.sectionAction(action, section.id)}
            onDragStart=${(/** @type {DragEvent} */ event) => editor.startDrag({ kind: 'SECTION', id: section.id }, event, /** @type {Element} */ (event.currentTarget).closest('.email-canvas-section'))}
            onDragEnd=${editor.endDrag}
          />`
        : html`<button type="button" class="email-section-tab" aria-label=${t('emailDesign.selectSection', { position: sectionIndex + 1 })}
            onClick=${(/** @type {MouseEvent} */ event) => { event.stopPropagation(); editor.select({ kind: 'SECTION', id: section.id }); }}>
            <${Icon} name="IconLayoutRows" size=${12} /><span>${t('emailDesign.sectionLabel', { position: sectionIndex + 1 })}</span>
          </button>`
      : null}
    <div class="email-canvas-section-row" style=${{ padding: paddingStyle(section.padding), background: section.backgroundColor ?? undefined }}>
      ${section.columns.map((column, columnIndex) => {
        const columnWidth = Math.round((innerWidth * (widths[columnIndex] ?? 100)) / 100);
        const isColumnSelected = editor.selection?.kind === 'COLUMN' && editor.selection.sectionId === section.id && editor.selection.columnIndex === columnIndex;
        return html`<div
          key=${column.id}
          class="email-canvas-column"
          data-selected=${isColumnSelected ? 'true' : undefined}
          data-empty=${column.blocks.length === 0 ? 'true' : undefined}
          data-valign=${column.verticalAlign}
          style=${{ width: `${widths[columnIndex] ?? 100}%`, background: column.backgroundColor ?? undefined }}
          onClick=${(/** @type {MouseEvent} */ event) => {
            event.stopPropagation();
            editor.select({ kind: 'COLUMN', sectionId: section.id, columnIndex });
          }}
        >
          ${column.blocks.length === 0
            ? editor.dragging
              ? html`<${InsertZone} editor=${editor} target=${{ kind: 'BLOCK', target: { sectionId: section.id, columnIndex, index: 0 } }} />`
              : html`<div class="email-empty-column">
                  <span>${t('emailDesign.emptyColumn')}</span>
                  ${!editor.readOnly
                    ? html`<button type="button" class="email-insert-button" aria-label=${t('emailDesign.toolbar.add')} title=${t('emailDesign.toolbar.add')}
                        onClick=${(/** @type {MouseEvent} */ event) => {
                          event.stopPropagation();
                          editor.openInsertMenu(/** @type {HTMLElement} */ (event.currentTarget), { kind: 'BLOCK', target: { sectionId: section.id, columnIndex, index: 0 } });
                        }}><${Icon} name="IconPlus" size=${12} /></button>`
                    : null}
                </div>`
            : html`
                <${InsertZone} editor=${editor} target=${{ kind: 'BLOCK', target: { sectionId: section.id, columnIndex, index: 0 } }} />
                ${column.blocks.map((block, index) => html`
                  <${CanvasBlock} key=${block.id} block=${block} editor=${editor} columnWidth=${columnWidth} />
                  <${InsertZone} editor=${editor} target=${{ kind: 'BLOCK', target: { sectionId: section.id, columnIndex, index: index + 1 } }} />
                `)}
              `}
        </div>`;
      })}
    </div>
  </div>`;
}

/**
 * O e-mail inteiro no canvas.
 * @param {{ editor: CanvasEditor }} props
 */
export function BlockCanvas({ editor }) {
  const style = editor.document.style;
  const sections = editor.document.sections ?? [];
  return html`<div class="email-canvas" style=${{ background: style.backgroundColor }} onClick=${() => editor.select(null)}>
    <div
      class="email-canvas-content"
      style=${{
        background: style.contentBackgroundColor,
        color: style.textColor,
        fontFamily: EMAIL_FONT_STACKS[style.fontFamily],
        fontSize: `${style.fontSize}px`,
        '--email-link-color': resolveColor(style.linkColor, editor.brandColor),
      }}
    >
      ${sections.length === 0
        ? editor.dragging
          ? html`<${InsertZone} editor=${editor} target=${{ kind: 'SECTION', index: 0 }} variant="SECTION" />`
          : html`<div class="email-canvas-empty">
              <${Icon} name=${BLOCK_ICONS.TEXT} size=${24} />
              <span>${editor.readOnly ? t('emailDesign.emptyReadOnly') : t('emailDesign.emptyCanvas')}</span>
              ${!editor.readOnly ? html`<${InsertZone} editor=${editor} target=${{ kind: 'SECTION', index: 0 }} variant="SECTION" />` : null}
            </div>`
        : html`
            <${InsertZone} editor=${editor} target=${{ kind: 'SECTION', index: 0 }} variant="SECTION" />
            ${sections.map((section, index) => html`
              <${CanvasSection} key=${section.id} section=${section} sectionIndex=${index} editor=${editor} />
              <${InsertZone} editor=${editor} target=${{ kind: 'SECTION', index: index + 1 }} variant="SECTION" />
            `)}
          `}
    </div>
  </div>`;
}
